import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuth((s) => s.login);
  const mfaChallenge = useAuth((s) => s.mfaChallenge);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mfaToken) {
        await mfaChallenge(mfaToken, mfaCode);
        navigate('/app');
        return;
      }
      const result = await login(email, password);
      if (result.mfaRequired && result.challengeToken) {
        setMfaToken(result.challengeToken);
      } else {
        navigate('/app');
      }
    } catch (err: any) {
      setError(err.message ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h2 style={{ fontSize: 'var(--text-2xl)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
        {mfaToken ? 'Verify it\'s you' : 'Welcome back'}
      </h2>

      {!mfaToken && (
        <>
          <label style={labelStyle}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
        </>
      )}

      {mfaToken && (
        <label style={labelStyle}>
          <span>6-digit code from your authenticator app</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            required
            autoFocus
            style={{ ...inputStyle, letterSpacing: '0.3em', fontSize: 'var(--text-xl)' }}
          />
        </label>
      )}

      {error && (
        <div style={{ color: 'var(--color-feedback-error)', fontSize: 'var(--text-sm)' }}>{error}</div>
      )}

      <button type="submit" disabled={loading} style={buttonStyle}>
        {loading ? 'Signing in…' : mfaToken ? 'Verify' : 'Sign in'}
      </button>

      {!mfaToken && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
          New here? <Link to="/auth/register" style={{ color: 'var(--color-brand-primary)', fontWeight: 500 }}>Create an account</Link>
        </div>
      )}
    </form>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)',
  fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-base)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface-primary)',
  outline: 'none',
};

const buttonStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  background: 'var(--color-brand-primary)',
  color: 'white',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-base)',
  fontWeight: 600,
  cursor: 'pointer',
};
